import {useLayoutEffect, useState} from 'react';
import {createPortal} from 'react-dom';
import {Switch} from 'components/common/Switch.component';
import type {Translate} from 'types/myTasks';

// The breadcrumb row lives inside the navigation web component's shadow DOM, which has no
// slot for extra content - but it does carry the Gaia stylesheet, so a switch portalled in
// there is styled like any other. The breadcrumb list itself comes first, so the switch sits
// right after the "My tasks" link; the surrounding header row is the fallback, as it stretches
// the switch away to the far right.
const BREADCRUMBS_SELECTORS = ['.ga-breadcrumb', '.ga-page-header__menu-breadcrumbs'];

// The navigation renders its shadow DOM asynchronously, so the row rarely exists on mount:
// watch the next few frames for it, which is where it normally turns up, and keep checking at
// a slow beat after that. The inline rendering below is only a stand-in until the row shows up,
// so a navigation that is slower than the frame burst still ends up with the switch in place.
const FRAME_ATTEMPTS = 10;
const RETRY_INTERVAL = 1000;

interface BetaSwitchProps {
    isChecked: boolean;
    onChange: () => void;
    translate: Translate;
}

/**
 * The row sits in a shadow root nested inside the navigation's own (vsn-navigation ->
 * vsn-top-bar), and querySelector does not pierce shadow boundaries - so walk them.
 */
function findIn(root: ParentNode | null, selector: string, depth: number): HTMLElement | null {
    if (!root || depth > 4) return null;
    const found = root.querySelector<HTMLElement>(selector);
    if (found) return found;
    for (const element of root.querySelectorAll('*')) {
        const nested = element.shadowRoot && findIn(element.shadowRoot, selector, depth + 1);
        if (nested) return nested;
    }
    return null;
}

function findBreadcrumbs(): HTMLElement | null {
    const root = document.querySelector('vsn-navigation')?.shadowRoot;
    if (!root) return null;
    for (const selector of BREADCRUMBS_SELECTORS) {
        const found = findIn(root, selector, 0);
        if (found) return found;
    }
    return null;
}

/**
 * "Try the Beta version" switch, shown right after the My tasks breadcrumb.
 */
const BetaSwitch = ({isChecked, onChange, translate}: BetaSwitchProps) => {

    // undefined = still looking for the breadcrumb row, null = render inline instead
    const [host, setHost] = useState<HTMLElement | null | undefined>(undefined);

    useLayoutEffect(() => {
        let element: HTMLElement | null = null;
        let observer: MutationObserver | null = null;
        let frame = 0;
        let timer = 0;
        let frames = 0;

        const attach = () => {
            const breadcrumbs = findBreadcrumbs();
            if (!breadcrumbs) {
                // a navigation that is still rendering is worth the next frame; anything else waits,
                // rendering inline meanwhile, but the search carries on for as long as we are mounted
                if (document.querySelector('vsn-navigation') && frames++ < FRAME_ATTEMPTS) {
                    frame = requestAnimationFrame(attach);
                } else {
                    setHost(null);
                    timer = window.setTimeout(attach, RETRY_INTERVAL);
                }
                return;
            }

            element = document.createElement('div');
            element.setAttribute('data-cy', 'myTasks.betaSwitch');
            element.style.display = 'flex';
            // the shadow DOM inherits the 10px root font size, which the Gaia switch does not set
            element.style.fontSize = '14px';
            breadcrumbs.appendChild(element);
            setHost(element);

            // the navigation re-renders its header on navigation; put the switch back if it is dropped
            observer = new MutationObserver(() => {
                if (!element.isConnected) findBreadcrumbs()?.appendChild(element);
            });
            observer.observe(breadcrumbs.getRootNode(), {childList: true, subtree: true});
        };

        attach();

        return () => {
            cancelAnimationFrame(frame);
            clearTimeout(timer);
            observer?.disconnect();
            element?.remove();
        };
    }, []);

    if (host === undefined) return null;

    const control = (
        <Switch
            label={isChecked ? translate('myTasks.overview.beta') : translate('myTasks.overview.old')}
            isChecked={isChecked}
            isDisabled={false}
            onChange={onChange}
        />
    );

    return host
        ? createPortal(control, host)
        : <div className="my-tasks-beta-switch" data-cy="myTasks.betaSwitch">{control}</div>;
};

export default BetaSwitch;
